import React from 'react';
import {
  RestLossAversionCardTemplates,
  type RestLossAversionCard,
} from '@bbkAdminUtils/api-client/rest-la-cards-types';
import { type RestModalForwards } from '@bbkAdminUtils/api-client/rest-modals-cards-types';
import { GlobalLogger } from '@bbkAdminUtils/GlobalLogger';
import LossSimpleBlurb from '@bbkFunnelComponents/loss/blurb/loss-simple-blurb';
import LossFeatureComparison from '@bbkFunnelComponents/loss/feature-comparison/loss-feature-comparison';
import LossSimpleChipImages from '@bbkFunnelComponents/loss/image-list/loss-simple-chip-images';
import LossOneUpImage from '@bbkFunnelComponents/loss/one-up-image/loss-one-up-image';
import LossSimpleLink from '@bbkFunnelComponents/loss/simple-link/loss-simple-link';
import LossSimpleList from '@bbkFunnelComponents/loss/simple-list/loss-simple-list';
import LossSimpleTeam from '@bbkFunnelComponents/loss/team/loss-simple-team';
import LossTwitterCard from '@bbkFunnelComponents/loss/twitter/loss-twitter-card';
import LossSimpleChip from '@bbkFunnelComponents/loss/usage/loss-simple-chip';

type Props = {
  card: RestLossAversionCard;
  handleLossCardAction?: (opts: { action: RestModalForwards }) => void;
  isHorizontal: boolean;
};
// Renderer selection and metadata live at the same composition point.
export const getLossAversionDefinition = (card: RestLossAversionCard) => {
  switch (card.template) {
    case RestLossAversionCardTemplates.simple_chip:
      return {
        editorHints: LossSimpleChip.editorHints,
        render: ({
          handleLossCardAction,
          isHorizontal,
        }: Omit<Props, 'card'>) => (
          <LossSimpleChip
            card={card}
            handleLossCardAction={handleLossCardAction}
            isHorizontal={isHorizontal}
          />
        ),
      };
    case RestLossAversionCardTemplates.simple_list:
      return {
        editorHints: LossSimpleList.editorHints,
        render: ({
          handleLossCardAction,
          isHorizontal,
        }: Omit<Props, 'card'>) => (
          <LossSimpleList
            card={card}
            handleLossCardAction={handleLossCardAction}
            isHorizontal={isHorizontal}
          />
        ),
      };
    case RestLossAversionCardTemplates.feature_comparison:
      return {
        editorHints: LossFeatureComparison.editorHints,
        render: ({
          handleLossCardAction,
          isHorizontal,
        }: Omit<Props, 'card'>) => (
          <LossFeatureComparison
            card={card}
            handleLossCardAction={handleLossCardAction}
            isHorizontal={isHorizontal}
          />
        ),
      };
    case RestLossAversionCardTemplates.loss_simple_link:
      return {
        editorHints: LossSimpleLink.editorHints,
        render: ({
          handleLossCardAction,
          isHorizontal,
        }: Omit<Props, 'card'>) => (
          <LossSimpleLink
            card={card}
            handleLossCardAction={handleLossCardAction}
            isHorizontal={isHorizontal}
          />
        ),
      };
    case RestLossAversionCardTemplates.la_one_up_image:
      return {
        editorHints: LossOneUpImage.editorHints,
        render: ({ handleLossCardAction }: Omit<Props, 'card'>) => (
          <LossOneUpImage
            card={card}
            handleLossCardAction={handleLossCardAction}
          />
        ),
      };
    default:
      return undefined;
  }
};

export const LossAversionCardFactory: React.FC<Props> = ({
  card,
  handleLossCardAction,
  isHorizontal,
}) => {
  const definition = getLossAversionDefinition(card);
  if (definition)
    return definition.render({ handleLossCardAction, isHorizontal });
  switch (card.template) {
    // Below card isn't in use
    case RestLossAversionCardTemplates.simple_chip_images:
      return <LossSimpleChipImages card={card} />;
    case RestLossAversionCardTemplates.simple_team:
      return <LossSimpleTeam card={card} />;
    case RestLossAversionCardTemplates.simple_blurb:
      return <LossSimpleBlurb card={card} />;
    case RestLossAversionCardTemplates.la_twitter_card:
      return <LossTwitterCard card={card} />;
    default:
      GlobalLogger.error(`No LA card matched for ${card}`);
      return <span>No card matched</span>;
  }
};
